import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { DoubleLeftOutlined } from '@ant-design/icons';
import { Button } from "antd";
import axios from "axios";
import classnames from "classnames";
import dayjs, { Dayjs } from 'dayjs';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRoad } from '@fortawesome/free-solid-svg-icons/faRoad';
import { axiosErrorHandler, showErrorNotification } from "../../../../../swzutils";
import { AdvancedMarker, GoogleMap, Polyline } from '../../../components/googleMap';
import DriverLocationSlider from '../DriverLocationSlider';
import WaypointInfoWindow from '../WaypointInfoWindow';
import MarkerLargeBuilder from "../markers/MarkerLargeBuilder";
import MarkerSmallBuilder from "../markers/MarkerSmallBuilder";
import WaypointRouteMarkerBuilder from "../markers/WaypointRouteMarkerBuilder";
import VehicleRouteTimeline from "./VehicleRouteTimeline";
import { userHasPermission } from "../../../../../swzutils/UserPermissionsUtil";
import { loggedInUserSelector } from "../../../../../store/slice/auth";
import { useInterval } from "usehooks-ts";
import { CENTRE_OF_AUSTRALIA } from "../../../components/googleMap/Model";
import { AddressStatusEnum } from "../../../api/Representation";
import CachedTenantSettingsRepository from "../../../../../CachedTenantSettingsRepository";
import DriverLocationInfoWindow from "../DriverLocationInfoWindow";
import { useAppSelector } from "../../../../../store";
const DRIVER_MARKER_Z_INDEX = 10;
const DRIVER_MARKER_WHEN_SELECTED_Z_INDEX = 150; // Above Waypoint but only when selected
const WAYPOINT_MARKER_Z_INDEX = 99; // Above Driver Marker since they are smaller and get hidden on vehicle approach
const VehicleRouteMap = ({ settingsTenantId, vehicleId, vehicleHireId, workDate, onVehicleRouteLoaded }) => {
    const loggedInUser = useAppSelector(loggedInUserSelector);
    const [routeMarkers, setRouteMarkers] = useState([]);
    const [routePolyline, setRoutePolyline] = useState(null);
    const [vehicleRoute, setVehicleRoute] = useState(null);
    const [waypointDirectionLegs, setWaypointDirectionLegs] = useState([]);
    const [selectedWaypoint, setSelectedWaypoint] = useState(null);
    const [selectedRoutePolyline, setSelectedRoutePolyline] = useState(null);
    const [waypointEtas, setWaypointEtas] = useState(null);
    const [waypointMarkerRefs, setWaypointMarkerRefs] = useState(null);
    const [driverLocation, setDriverLocation] = useState(null);
    const [driverLocationInfoWindowVisible, setDriverLocationInfoWindowVisible] = useState(false);
    const [vehicleLocationHistory, setVehicleLocationHistory] = useState([]);
    const [showTraffic, setShowTraffic] = useState(false);
    const [liveTracking, setLiveTracking] = useState(false);
    const [hideTimeline, setHideTimeline] = useState(false);
    const [tenantSettings, setTenantSettings] = useState(null);
    const [mapRef, setMapRef] = useState(null);
    useEffect(() => {
        if (loggedInUser) {
            doLoad();
        }
    }, [loggedInUser, vehicleId, workDate]);
    useEffect(() => {
        if (settingsTenantId) {
            CachedTenantSettingsRepository.getSettingsForTenant(settingsTenantId)
                .then(setTenantSettings);
        }
        if (workDate?.isSame(dayjs(), 'day')) {
            loadDriverLocation();
        }
    }, [settingsTenantId]);
    useInterval(() => {
        loadDriverLocationAndHistory();
    }, liveTracking ? 30000 : null);
    useEffect(() => {
        if (mapRef && vehicleRoute) {
            if (!liveTracking || driverLocation) {
                queryGoogleDirectionsService();
            }
        }
    }, [vehicleRoute]);
    useEffect(() => {
        boundMap();
    }, [routeMarkers]);
    useEffect(() => {
        boundMap();
        if (!liveTracking && vehicleRoute) {
            queryGoogleDirectionsService();
        }
        else if (liveTracking && vehicleRoute && driverLocation) {
            queryGoogleDirectionsService();
        }
    }, [mapRef]);
    const doLoad = () => {
        if (workDate?.isSame(dayjs(), 'day')) {
            setLiveTracking(true);
            loadRoute();
            loadDriverLocationAndHistory();
        }
        else {
            setLiveTracking(false);
            setDriverLocation(null);
            loadRoute();
            if (workDate && workDate.isBefore(dayjs(), 'day')) {
                loadDriverLocationHistory();
            }
        }
    };
    const loadRoute = () => {
        if (!userHasPermission(loggedInUser, ['admin:vehicle:deliveries'])) {
            return;
        }
        let uri = `/admin/v1/vehicles/${vehicleId}/deliveries/route`;
        if (workDate) {
            uri += "?workDate=" + workDate.format("YYYY-MM-DD");
            if (vehicleHireId) {
                uri += "&vehicleHireId=" + vehicleHireId;
            }
        }
        axios.get(uri)
            .then(response => {
            const vehicleRoute = response.data;
            if (vehicleRoute.waypoints == null || vehicleRoute.waypoints.length === 0) {
                setHideTimeline(true);
            }
            setVehicleRoute(vehicleRoute);
            onVehicleRouteLoaded && onVehicleRouteLoaded(vehicleRoute);
            addRouteMarkers(vehicleRoute);
        })
            .catch(axiosErrorHandler);
    };
    const loadDriverLocationAndHistory = () => {
        loadDriverLocation();
        loadDriverLocationHistory();
    };
    const loadDriverLocation = () => {
        axios.get(`/admin/v1/drivers/locations?vehicleId=${vehicleId}&includeContractors=true`)
            .then(response => {
            if (response.data && response.data.results && response.data.results.length > 0) {
                const driverLocation = response.data.results[0];
                setDriverLocation({
                    ...driverLocation,
                    markerRef: React.createRef()
                });
            }
            else {
                // No driver logged in for this vehicle so clear live tracking
                setLiveTracking(false);
                setDriverLocation(null);
            }
            if (mapRef && vehicleRoute) {
                queryGoogleDirectionsService();
            }
        })
            .catch((error) => {
            setLiveTracking(false);
            axiosErrorHandler(error);
        });
    };
    const loadDriverLocationHistory = () => {
        let uri = `/admin/v1/vehicles/${vehicleId}/location-history`;
        if (workDate) {
            uri += "?workDate=" + workDate.format("YYYY-MM-DD");
        }
        axios.get(uri)
            .then(response => setVehicleLocationHistory(response.data))
            .catch((error) => {
            setLiveTracking(false);
            axiosErrorHandler(error);
        });
    };
    const addRouteMarkers = (vehicleRoute) => {
        if (!vehicleRoute.waypoints || !vehicleRoute.waypoints.length) {
            setRouteMarkers([]);
            return;
        }
        const markerRefs = {};
        const markers = vehicleRoute.waypoints
            .filter(waypoint => waypoint.address.latitude && waypoint.address.longitude)
            .map((waypoint) => {
            const showTick = waypoint.status === 'COMPLETED';
            const isDelivery = waypoint.task === "UNLOAD";
            const markerNum = isDelivery ? waypoint.sequence : null;
            const markerSvg = WaypointRouteMarkerBuilder.buildMarker(markerNum, isDelivery, showTick);
            markerRefs[waypoint.id] = React.createRef();
            return (_jsx(AdvancedMarker, { ref: markerRefs[waypoint.id], onClick: () => onWaypointSelected(waypoint), imageUri: markerSvg, position: {
                    lat: waypoint.address.latitude,
                    lng: waypoint.address.longitude
                }, zIndex: WAYPOINT_MARKER_Z_INDEX }, waypoint.id));
        });
        setWaypointMarkerRefs(markerRefs);
        setRouteMarkers(markers);
    };
    const onVehicleMarkerSelected = () => {
        let selectedRoutePolyline = null;
        if (waypointDirectionLegs && waypointDirectionLegs.length > 0) {
            selectedRoutePolyline = buildPolylineForLeg(waypointDirectionLegs[0]);
        }
        setSelectedRoutePolyline(selectedRoutePolyline);
        setDriverLocationInfoWindowVisible(true);
    };
    const onWaypointSelected = (selectedWaypoint) => {
        if (selectedWaypoint.address.status === AddressStatusEnum.INVALID || !selectedWaypoint.address.latitude || !selectedWaypoint.address.longitude) {
            return;
        }
        setSelectedRoutePolyline(null);
        waypointDirectionLegs.forEach(leg => {
            if (leg.sourceWaypoint && leg.sourceWaypoint.id === selectedWaypoint.id) {
                setSelectedRoutePolyline(buildPolylineForLeg(leg));
            }
        });
        setSelectedWaypoint(selectedWaypoint);
    };
    const buildPolylineForLeg = (leg) => {
        if (leg.steps.length <= 1) {
            return null;
        }
        return (_jsx(Polyline, { strokeColor: "#000", path: leg.steps.flatMap(step => step.path) }, "selected_route_polyline"));
    };
    const determineWaypointEtas = (directionLegs) => {
        // HACK ALERT: The client is calculating the real time ETA's from a Google directions route call
        //             made in the client.  If we remove this client logic to use server real time ETA's then
        //             we have to make two calls every 30 seconds, one to the server to get real time ETA's
        //             and a second to google to get overall route and leg polyline.  This effectively doubles
        //             the Google map costs for this page and also puts more load on the server.
        const now = dayjs();
        let eta = dayjs();
        //console.log("BEGIN>--------------------------------------------");
        //console.log("calcRealtimeEtaToSelectedWaypoint now:" + eta.format("HH:mm:ss"));
        const waypointEtas = {};
        directionLegs.forEach(leg => {
            if (leg.duration) {
                eta = eta.add(leg.duration.value, "seconds");
            }
            //console.log("leg", leg);
            //console.log("add leg duration " + leg.duration.text + " new eta:" + eta.format("HH:mm:ss"));
            if (leg.sourceWaypoint) {
                waypointEtas[leg.sourceWaypoint.id] = eta.format("HH:mm") + " (" + dayjs.duration(eta.diff(now)).humanize() + " from now)";
                //console.log("sourceWaypoint.id:" + leg.sourceWaypoint.id + "=" + waypointEtas[leg.sourceWaypoint.id]);
            }
            if (leg.sourceWaypoint && leg.sourceWaypoint.loadingUnloadingSeconds) {
                eta = eta.add(leg.sourceWaypoint.loadingUnloadingSeconds, "seconds");
                //console.log("add loadingUnloadingSeconds " + leg.sourceWaypoint.loadingUnloadingSeconds + " new eta:" + eta.format("HH:mm:ss"));
            }
        });
        setWaypointEtas(waypointEtas);
        //console.log("END<--------------------------------------------");
    };
    const queryGoogleDirectionsService = () => {
        const waypoints = vehicleRoute?.waypoints;
        if (!waypoints || waypoints.length < 2) {
            // Need at least 2 waypoints to draw a route
            return;
        }
        if (workDate && workDate.isBefore(dayjs(), 'day')) {
            // Don't query the proposed route for delivery dates in the past
            return;
        }
        const invalidAddressCount = waypoints
            .filter(w => w.address.status === AddressStatusEnum.INVALID || !w.address.latitude || !w.address.longitude)
            .length;
        if (invalidAddressCount > 0) {
            showErrorNotification("The route could not be calculated as it contains one or more invalid addresses.");
            return;
        }
        if (!google.maps) {
            return;
        }
        const googleWaypoints = buildGoogleWaypointsArray(waypoints);
        const last = waypoints.length - 1;
        const directionsService = new google.maps.DirectionsService();
        const destinationWaypoint = waypoints[last];
        directionsService.route({
            origin: determineDirectionsOrigin(waypoints),
            destination: {
                lat: destinationWaypoint.address.latitude, // validated above
                lng: destinationWaypoint.address.longitude
            },
            waypoints: googleWaypoints.map(waypoint => {
                return {
                    location: {
                        lat: waypoint.address.latitude, // validated above
                        lng: waypoint.address.longitude
                    },
                    stopover: true
                };
            }),
            optimizeWaypoints: false,
            travelMode: google.maps.TravelMode.DRIVING,
            unitSystem: google.maps.UnitSystem.METRIC,
            provideRouteAlternatives: false,
            avoidFerries: true,
            avoidHighways: false,
            avoidTolls: false
        }).then((response) => {
            const route = response.routes[0];
            setRoutePolyline(_jsx(Polyline, { path: route.overview_path }, "overview_polyline"));
            const waypointDirectionLegs = route.legs
                .map((leg, idx) => {
                const lastLeg = route.legs.length - 1;
                return {
                    ...leg,
                    sourceWaypoint: idx === lastLeg ? destinationWaypoint : googleWaypoints[idx]
                };
            });
            setWaypointDirectionLegs(waypointDirectionLegs);
            if (liveTracking) {
                determineWaypointEtas(waypointDirectionLegs);
            }
        }).catch(reason => console.error(reason));
    };
    const buildGoogleWaypointsArray = (waypoints) => {
        const googleWaypoints = [];
        if (waypoints.length > 2 || (waypoints.length > 1 && driverLocation)) {
            // don't add the first waypoint for when not live tracking as it's used as the origin.
            const first = liveTracking ? 0 : 1;
            // don't add the last waypoint as it's used as the destination
            const last = waypoints.length - 1;
            // Exclude COMPLETED waypoints for live tracking
            const includeCompleted = !liveTracking;
            for (let i = first; i < last; i++) {
                const waypoint = waypoints[i];
                if (!includeCompleted && waypoint.status === "COMPLETED") {
                    continue;
                }
                googleWaypoints.push(waypoint);
            }
        }
        return googleWaypoints;
    };
    const determineDirectionsOrigin = (waypoints) => {
        if (driverLocation) {
            return {
                lat: driverLocation.latitude,
                lng: driverLocation.longitude
            };
        }
        return {
            lat: waypoints[0].address.latitude,
            lng: waypoints[0].address.longitude
        };
    };
    const onMapClicked = () => {
        setDriverLocationInfoWindowVisible(false);
        setSelectedWaypoint(null);
    };
    const toggleOpenCloseTimelinePanel = () => {
        setHideTimeline(!hideTimeline);
    };
    const boundMap = () => {
        if (!mapRef) {
            return; // Google Maps is not yet loaded
        }
        const bounds = new google.maps.LatLngBounds();
        if (vehicleRoute && vehicleRoute.waypoints && vehicleRoute.waypoints.length) {
            vehicleRoute.waypoints.forEach((waypoint) => {
                if (waypoint.address.latitude && waypoint.address.longitude) {
                    const latLng = new google.maps.LatLng(waypoint.address.latitude, waypoint.address.longitude);
                    bounds.extend(latLng);
                }
            });
        }
        if (vehicleLocationHistory && vehicleLocationHistory.length) {
            vehicleLocationHistory.forEach(location => {
                const latLng = new google.maps.LatLng(location.latitude, location.longitude);
                bounds.extend(latLng);
            });
        }
        if (driverLocation) {
            const latLng = new google.maps.LatLng(driverLocation.latitude, driverLocation.longitude);
            bounds.extend(latLng);
        }
        mapRef.fitBounds(bounds);
    };
    const driverLocationMarkerZIndex = driverLocationInfoWindowVisible ? DRIVER_MARKER_WHEN_SELECTED_Z_INDEX : DRIVER_MARKER_Z_INDEX;
    const selectedWaypointMarker = selectedWaypoint && waypointMarkerRefs && waypointMarkerRefs[selectedWaypoint.id];
    const hasRoutePermission = userHasPermission(loggedInUser, ['admin:vehicle:deliveries']);
    const humanizedWaypointEta = selectedWaypoint && liveTracking && waypointEtas && waypointEtas[selectedWaypoint.id];
    return (_jsxs("div", { className: classnames('route-map-container', { 'route-timeline-hide': hideTimeline || !hasRoutePermission }), children: [hasRoutePermission &&
                _jsxs(_Fragment, { children: [_jsx("div", { className: "map-show-timeline", children: _jsx(Button, { type: "primary", htmlType: "button", icon: _jsx(DoubleLeftOutlined, {}), onClick: toggleOpenCloseTimelinePanel }) }), _jsx(VehicleRouteTimeline, { vehicleRoute: vehicleRoute, waypointEtas: waypointEtas, selectedWaypointId: selectedWaypoint?.id, onClose: toggleOpenCloseTimelinePanel, onWaypointSelected: onWaypointSelected })] }), _jsxs("div", { className: "map-body", children: [mapRef &&
                        _jsx("div", { id: "map-option-buttons", children: _jsx("button", { className: classnames('btn', {
                                    "on": showTraffic,
                                    "off": !showTraffic
                                }), onClick: () => setShowTraffic(!showTraffic), children: _jsx(FontAwesomeIcon, { icon: faRoad }) }) }), _jsxs(GoogleMap, { onLoad: setMapRef, initialCenter: CENTRE_OF_AUSTRALIA, centerAroundCurrentLocation: false, boundMapToMarkers: false, showTraffic: showTraffic, onClick: onMapClicked, children: [routeMarkers, !selectedRoutePolyline && routePolyline, selectedRoutePolyline, driverLocation &&
                                _jsx(AdvancedMarker, { onClick: onVehicleMarkerSelected, imageUri: driverLocation.vehicle && driverLocation.vehicle.registration
                                        ? MarkerLargeBuilder.buildMarker(driverLocation, loggedInUser, settingsTenantId)
                                        : MarkerSmallBuilder.buildMarker(driverLocation), data: driverLocation, title: driverLocation.vehicle && driverLocation.vehicle.type.description, ref: driverLocation.markerRef, zIndex: driverLocationMarkerZIndex, position: {
                                        lat: driverLocation.latitude,
                                        lng: driverLocation.longitude
                                    } }, driverLocation.id), _jsx(DriverLocationInfoWindow, { driverLocation: driverLocation, showConsignmentSummary: true, showVehicleHireSummary: false, markerRef: driverLocation?.markerRef, visible: driverLocationInfoWindowVisible, onClose: () => setDriverLocationInfoWindowVisible(false) }), _jsx(WaypointInfoWindow, { windowKey: "WaypointInfoWindow", routeStatus: selectedWaypoint?.status, humanizedEta: humanizedWaypointEta, markerRef: selectedWaypointMarker, companyName: selectedWaypoint?.companyName, contactName: selectedWaypoint?.contactName, contactTelephone: selectedWaypoint?.contactTelephone, address: selectedWaypoint?.address, arrivedAt: selectedWaypoint?.arrivedAt, visible: !!selectedWaypoint, onClose: () => setSelectedWaypoint(null), renderFooter: () => {
                                    if (!selectedWaypoint?.completedAt) {
                                        return;
                                    }
                                    return (_jsxs("dl", { children: [_jsx("dt", { children: "Completed at" }), _jsx("dd", { children: dayjs(selectedWaypoint.completedAt).format("DD/MM/YYYY HH:mm") })] }));
                                } }), vehicleLocationHistory.length && tenantSettings &&
                                _jsx(DriverLocationSlider, { driverLocationHistory: vehicleLocationHistory, driverIdleAlertMinutes: tenantSettings.driverIdleAlertMinutes })] })] })] }));
};
export default VehicleRouteMap;
